import {
  creatorPublicPathOmitsUsernamePrefix,
  getCreatorSpaRelativePath,
} from "../url/getCreatorSpaRelativePath";

/** True when public paths should omit `/:username` (custom domain, creator subdomain, dev slug.localhost). */
export function isCustomDomain(hostname?: string): boolean {
  return creatorPublicPathOmitsUsernamePrefix(hostname);
}

export function getPublicUrl(username: string, route: string, from?: string): string {
  const path = getCreatorSpaRelativePath({
    username,
    route,
    pathname: typeof window !== "undefined" ? window.location.pathname : "/",
  });
  return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function getPublicUrlWithUsername(username: string, route: string): string {
  return getCreatorSpaRelativePath({
    username,
    route,
    pathname: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

export const CREATOR_PAGE_COMPONENT_QUERY_PARAM = "component";

/**
 * Public creator page URLs:
 * - Legacy: `/pages/:pageSlug/:username`
 * - Path hosts: `/:username/page/:pageSlug`
 * - Custom domain / subdomain: `/page/:pageSlug` (spoofed to `/:username/page/:pageSlug`)
 */
export function isCreatorPagePublicPathname(pathname?: string): boolean {
  const p = (pathname || "")
    .split("?")[0]
    .replace(/\/$/, "");
  if (p.startsWith("/pages/")) {
    return true;
  }
  if (/^\/page\/[^/]+$/.test(p)) {
    return true;
  }
  if (/^\/website\/[^/]+\/page\/[^/]+/.test(p)) {
    return true;
  }
  return /^\/[^/]+\/page\/[^/]+/.test(p);
}

/** Public path for a creator page slug (`/:username/page/:pageSlug` or custom-domain `/page/:pageSlug`). */
export function buildCreatorPagePublicPath(
  username: string,
  pageSlug: string,
): string {
  const slug = encodeURIComponent(String(pageSlug || "").trim());
  if (!slug) return "/";

  if (isCustomDomain()) {
    return `/page/${slug}`;
  }

  const user = encodeURIComponent(String(username || "").trim());
  return `/${user}/page/${slug}`;
}

export function getCreatorPageBasePath(
  location?: any,
  creatorPageSlug?: string | null,
): string | null {
  const pathname = (location?.pathname || window.location.pathname || "")
    .split("?")[0]
    .replace(/\/$/, "");

  if (pathname.startsWith("/pages/")) {
    return pathname || "/";
  }

  const newStyle = pathname.match(/^(\/[^/]+\/page\/[^/]+)/);
  if (newStyle) {
    return newStyle[1] || pathname;
  }

  // Custom-domain public path before host spoof: `/page/:pageSlug`
  const customDomainPage = pathname.match(/^(\/page\/[^/]+)$/);
  if (customDomainPage) {
    return customDomainPage[1] || pathname;
  }

  const normalizedPageSlug = String(creatorPageSlug || "")
    .trim()
    .toLowerCase();
  if (normalizedPageSlug) {
    const userPageMatch = pathname.match(/^\/([^/]+)\/page\/([^/]+)$/);
    if (
      userPageMatch &&
      userPageMatch[2].toLowerCase() === normalizedPageSlug
    ) {
      return pathname;
    }

    if (isCustomDomain()) {
      const pageSegment = pathname.match(/^\/page\/([^/]+)$/);
      if (
        pageSegment &&
        pageSegment[1].toLowerCase() === normalizedPageSlug
      ) {
        return pathname;
      }
    }
  }

  return null;
}

export function getCreatorPageComponentPath(
  componentSlug: string,
  location?: any,
  creatorPageSlug?: string | null,
  username?: string | null,
): string | null {
  const normalizedComponentSlug = String(componentSlug || "").trim();
  let creatorPageBasePath = getCreatorPageBasePath(location, creatorPageSlug);

  if (!creatorPageBasePath && creatorPageSlug && username) {
    creatorPageBasePath = buildCreatorPagePublicPath(username, creatorPageSlug);
  }

  if (!creatorPageBasePath || !normalizedComponentSlug) {
    return null;
  }

  const searchParams = new URLSearchParams(
    location?.search || window.location.search || "",
  );

  searchParams.delete("term");
  searchParams.delete("from");
  searchParams.set(
    CREATOR_PAGE_COMPONENT_QUERY_PARAM,
    normalizedComponentSlug,
  );

  return `${creatorPageBasePath}?${searchParams.toString()}`;
}

/**
 * Public "back to this surface" path.
 * On `/page/:page-slug` (and custom-domain `/page/:slug`), stay on that page
 * (search, close-search, and the individual-view home icon).
 * Link-in-bio, `/:username/:slug` individual, and embed still resolve to home.
 */
export function getCreatorPageOrHomePath(
  username: string,
  location?: any,
  from?: string,
): string {
  const pathname = (
    location?.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "") ||
    ""
  )
    .split("?")[0]
    .replace(/\/$/, "");

  if (!isCreatorPagePublicPathname(pathname)) {
    return getHomePath(username, from, location);
  }

  // Keep legacy `/pages/:slug/:username` as-is.
  if (pathname.startsWith("/pages/")) {
    return pathname || "/";
  }

  const pageSlugMatch = pathname.match(/\/page\/([^/]+)$/);
  const pageSlug = pageSlugMatch?.[1];
  if (pageSlug) {
    return buildCreatorPagePublicPath(username, decodeURIComponent(pageSlug));
  }

  return getCreatorPageBasePath(location) || pathname;
}

export function getHomePath(
  username: string,
  _from?: string,
  location?: any,
  _options?: { creatorPageSlug?: string | null },
): string {
  // Home always resolves to the main link-in-bio profile. Do not route through an
  // intermediate creator-page URL (component → creator page → link-in-bio required
  // two home clicks when a collection also has a published creator page).

  const pathname =
    location?.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  // Website-template SPA paths (incl. /website/, /link-in-bio) via getCreatorSpaRelativePath;
  // also covers embed/preview/creator base segments that the legacy basePath logic handled.
  if (pathname.includes("/link-in-bio")) {
    return getCreatorSpaRelativePath({
      username,
      route: "link-in-bio",
      pathname,
    });
  }
  return getCreatorSpaRelativePath({ username, pathname });
}

export function getContactMePath(username: string, location?: any): string {
  const pathname =
    location?.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  return getCreatorSpaRelativePath({ username, route: "contact-me", pathname });
}
